// Scalar string funcs — `concat`, `upper`, `lower`, `substring`,
// `length`, `trim`, `contains`, `starts-with`, `ends-with`, `replace`,
// `split`, `index-of`. Registered in `opMeta` (NOT a verb registry),
// so they are usable in any expression context: derive bodies, filter
// predicates, top-level bindings. Composed into `transformOps` so the
// generated LLM DSL reference (`canonicalDslScope`) surfaces them.
//
// Null convention (pinned): a null/undefined SUBJECT (first arg)
// short-circuits — `null` for value-returning ops, `false` for the
// predicate-shaped ops (contains/starts-with/ends-with), `-1` for
// `index-of`. `concat` is the exception: it is forgiving (built for
// data-cleaning) and coerces null args to `""`.

import { z } from 'zod';

import { instanceKey, isInstanceReference } from '#core/schema/utils.ts';

import { defineFold } from '../../builtins/helpers.ts';
import { opMeta } from '../../op-meta.ts';
import { booleanCol, type ColumnType, numberCol, stringCol, unknownCol } from '../../schema/types.ts';
import type { Value } from '../../value.ts';
import { dateAwareEquals } from './date-utils.ts';

/**
 * Single element-equality SSOT for array membership. Two CDF node
 * identities (`{space, externalId}` direct-relation structs) are equal
 * by their canonical `instanceKey` — structural, NOT reference identity
 * — so equal-content distinct refs match, mirroring the struct-aware
 * dedup `boundedDistinctSet` uses and the `containsAny` semantics pushed
 * down over relation lists. Every other pair delegates to the engine's
 * `in`/`=` equality (`dateAwareEquals`).
 */
const memberEquals = (member: unknown, element: unknown): boolean => {
  if (isInstanceReference(member) && isInstanceReference(element)) {
    return instanceKey(member) === instanceKey(element);
  }
  return dateAwareEquals(member, element);
};

/**
 * SSOT for "does this array CONTAINER hold this ELEMENT". Element
 * comparison reuses the engine's `in`/`=` equality: strict for
 * number/boolean, case-insensitive for strings, date-aware for
 * dates/timestamps, and structural (`instanceKey`) for instance-ref
 * structs — so `contains` over an array matches `(in element container)`
 * exactly, including relation LISTs (`array<struct{space,externalId}>`).
 * Shared by `contains`'s in-memory array dispatch and the residual
 * (non-pushed) array-membership path so pushed and residual results
 * agree.
 */
export const arrayContainsMember = (container: readonly unknown[], element: unknown): boolean =>
  container.some((member) => memberEquals(member, element));

/**
 * Build a null-propagating string op. The first positional is the
 * SUBJECT: a null/undefined subject short-circuits to `whenNull`
 * (default `null`); otherwise the subject is `String()`-coerced and
 * the remaining raw args are handed to `fn`. Built on the shared
 * `defineFold` so the apply path (schema-resolved params) and the
 * compileExpr path (per-row values) share ONE reducer — no per-op
 * apply/compileExpr duplication. Schemas use `z.unknown()` params so
 * a null survives to the reducer instead of being coerced away.
 */
const subjectOp = (
  fn: (subject: string, rest: readonly unknown[]) => Value,
  resultType: ColumnType,
  whenNull: Value = null,
) =>
  defineFold<unknown>(
    (vs) => {
      const [subject, ...rest] = vs;
      return subject === null || subject === undefined ? whenNull : fn(String(subject), rest);
    },
    undefined,
    resultType,
  );

export const concat = z
  .object({ name: z.literal('concat'), params: z.array(z.unknown()).min(1) })
  .describe(
    'Concatenate 1+ values into a string. Null/undefined args are treated as "" (forgiving — built for data-cleaning).',
  )
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(concat "a" "b" "c")', result: '"abc"' },
    ...defineFold<unknown>(
      (vs) => vs.map((v) => (v === null || v === undefined ? '' : String(v))).join(''),
      undefined,
      stringCol,
    ),
  });

export const upper = z
  .object({ name: z.literal('upper'), params: z.tuple([z.unknown()]) })
  .describe('Uppercase a string.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(upper "ab")', result: '"AB"' },
    ...subjectOp((s) => s.toUpperCase(), stringCol),
  });

export const lower = z
  .object({ name: z.literal('lower'), params: z.tuple([z.unknown()]) })
  .describe('Lowercase a string.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(lower "AB")', result: '"ab"' },
    ...subjectOp((s) => s.toLowerCase(), stringCol),
  });

export const trim = z
  .object({ name: z.literal('trim'), params: z.tuple([z.unknown()]) })
  .describe('Strip leading/trailing whitespace.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(trim "  hi  ")', result: '"hi"' },
    ...subjectOp((s) => s.trim(), stringCol),
  });

export const length = z
  .object({ name: z.literal('length'), params: z.tuple([z.unknown()]) })
  .describe('String length.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(length "abcd")', result: '4' },
    ...subjectOp((s) => s.length, numberCol),
  });

export const substring = z
  .object({ name: z.literal('substring'), params: z.array(z.unknown()).min(2).max(3) })
  .describe('(substring s start [end]) — JS .substring semantics (end-exclusive, optional).')
  .register(opMeta, {
    example: { expr: '(substring "hello" 1 3)', result: '"el"' },
    ...subjectOp(
      (s, rest) =>
        rest[1] === undefined ? s.substring(Number(rest[0])) : s.substring(Number(rest[0]), Number(rest[1])),
      stringCol,
    ),
  });

export const replace = z
  .object({ name: z.literal('replace'), params: z.tuple([z.unknown(), z.unknown(), z.unknown()]) })
  .describe('(replace s from to) — replace ALL literal occurrences (not a regex).')
  .register(opMeta, {
    example: { expr: '(replace "a-b-c" "-" "_")', result: '"a_b_c"' },
    ...subjectOp((s, rest) => s.split(String(rest[0])).join(String(rest[1])), stringCol),
  });

export const contains = z
  .object({ name: z.literal('contains'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe(
    '(contains subject x) — type-directed: a STRING subject tests substring; an ARRAY subject tests element membership (same equality as `in`). Null subject ⇒ false.',
  )
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(contains "hello" "ell")', result: 'true' },
    ...defineFold<unknown>(
      (vs) => {
        const [subject, x] = vs;
        if (subject === null || subject === undefined) {
          return false;
        }
        if (Array.isArray(subject)) {
          return arrayContainsMember(subject, x);
        }
        return String(subject).includes(String(x));
      },
      undefined,
      booleanCol,
    ),
  });

export const startsWith = z
  .object({ name: z.literal('starts-with'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(starts-with s prefix) — Null subject ⇒ false.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(starts-with "hello" "he")', result: 'true' },
    ...subjectOp((s, rest) => s.startsWith(String(rest[0])), booleanCol, false),
  });

export const endsWith = z
  .object({ name: z.literal('ends-with'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(ends-with s suffix) — Null subject ⇒ false.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(ends-with "hello" "lo")', result: 'true' },
    ...subjectOp((s, rest) => s.endsWith(String(rest[0])), booleanCol, false),
  });

export const indexOf = z
  .object({ name: z.literal('index-of'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(index-of s sub) — 0-based index of first occurrence, or -1. Null subject ⇒ -1.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(index-of "hello" "l")', result: '2' },
    ...subjectOp((s, rest) => s.indexOf(String(rest[0])), numberCol, -1),
  });

export const split = z
  .object({ name: z.literal('split'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(split s sep) — split into an array of substrings.')
  .register(opMeta, {
    example: { expr: '(split "a,b,c" ",")', result: '["a","b","c"]' },
    ...subjectOp((s, rest) => s.split(String(rest[0])), unknownCol),
  });

// Named `str-join` (NOT `join`) to avoid colliding with the relational
// `join` op. Null array ⇒ null; null/undefined elements are skipped so a
// sparse array still renders cleanly.
export const strJoin = z
  .object({ name: z.literal('str-join'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(str-join arr sep) — join array elements with sep. Null elements are skipped; a null array ⇒ null.')
  .register(opMeta, {
    terseGroup: 'string',
    example: { expr: '(str-join (col % assets name) " / ")', result: '"ADNOC Offshore / A-OF"' },
    ...defineFold<unknown>(
      (vs) => {
        const [arr, sep] = vs;
        if (arr === null || arr === undefined) {
          return null;
        }
        return (arr as readonly unknown[])
          .filter((el) => el !== null && el !== undefined)
          .map((el) => String(el))
          .join(String(sep));
      },
      undefined,
      stringCol,
    ),
  });

/** All scalar string-func schemas — composed into `transformOps`. */
export const stringFuncs = [
  concat,
  upper,
  lower,
  trim,
  length,
  substring,
  replace,
  contains,
  startsWith,
  endsWith,
  indexOf,
  split,
  strJoin,
] as const;
